import {
  AuditLiveMention,
  BaseAPIClient,
  CreatePntsDto,
  CreateScheduleDtoV2,
  DateTimeStr,
  EqualMd5AudiosReportDto,
  GetAuditConfigDto,
  GlobalNoMatchesReportDto,
  GlobalSchedulesNoMatchesReport,
  MissingAudiosReportDto,
  NoMatchesReport,
  ScheduleStatusReport,
  UpdateAuditConfigDto,
} from '@mi-front/core';

/**
 * Legacy audit endpoints without a generated api-v2 client yet.
 * Prefer `@mi-front/api-v2` for matches, schedules, media, GIP metadata, and PNT overlays.
 */
export const AuditAPI = {
  updateLiveMentionMatch(
    this: BaseAPIClient,
    args: {
      stationId: string;
      id: string;
      data: Partial<AuditLiveMention>;
    },
  ): Promise<void> {
    const { stationId, id, data } = args;

    return this.put(`/v2/stations/${stationId}/audit/live-mentions/${id}`, data);
  },

  stationReportsEqualMd5(
    this: BaseAPIClient,
    args: {
      stationId: string;
      from: DateTimeStr;
      to: DateTimeStr;
    },
  ): Promise<EqualMd5AudiosReportDto> {
    const { stationId, from, to } = args;

    return this.get(`/v2/stations/${stationId}/audit/reports/equal-md5`, {
      params: {
        from,
        to,
      },
    });
  },

  stationReportsNoMatches(
    this: BaseAPIClient,
    args: {
      stationId: string;
      audioFrom: DateTimeStr;
      audioTo: DateTimeStr;
      matchFrom: DateTimeStr;
      matchTo: DateTimeStr;
    },
  ): Promise<NoMatchesReport> {
    const { stationId, audioFrom, audioTo, matchFrom, matchTo } = args;

    return this.get(`/v2/stations/${stationId}/audit/reports/no-matches`, {
      params: {
        audio_from: audioFrom,
        audio_to: audioTo,
        match_from: matchFrom,
        match_to: matchTo,
      },
    });
  },

  stationReportsMissingAudios(
    this: BaseAPIClient,
    args: {
      stationId: string;
      from: DateTimeStr;
      to: DateTimeStr;
    },
  ): Promise<MissingAudiosReportDto> {
    const { stationId, from, to } = args;

    return this.get(`/v2/stations/${stationId}/audit/reports/missing-audios-schedule`, {
      params: {
        from,
        to,
      },
    });
  },

  stationReportsScheduleStatusAds(
    this: BaseAPIClient,
    stationId: string,
    params: {
      from: DateTimeStr;
      to: DateTimeStr;
      customId?: string;
    },
  ): Promise<ScheduleStatusReport> {
    return this.get(`/v2/stations/${stationId}/audit/reports/schedule-status`, {
      params,
    });
  },

  reportMissingAudios(
    this: BaseAPIClient,
    data: {
      stationsIds: string[];
      from: DateTimeStr;
      to: DateTimeStr;
      groupBy: string;
      options: {
        format?: string;
        timezone?: string;
      };
    },
  ): Promise<MissingAudiosReportDto> {
    return this.post(`/v2/audit/reports/missing-audios`, data, {
      responseType: data.options.format === 'xls' ? 'arraybuffer' : undefined,
    });
  },

  reportGlobalNoMatches(
    this: BaseAPIClient,
    data: {
      audioRange: {
        from: DateTimeStr;
        to: DateTimeStr;
      };
      options: {
        format?: string;
      };
    },
  ): Promise<GlobalNoMatchesReportDto> {
    return this.post(`/v2/audit/reports/audios-no-matches`, data, {
      responseType: data.options.format === 'xls' ? 'arraybuffer' : undefined,
    });
  },

  reportGlobalSchedulesNoMatches(
    this: BaseAPIClient,
    data: {
      stationsIds: string[];
      from: DateTimeStr;
      to: DateTimeStr;
      includeMissingAudio?: boolean;
      page?: number;
      pageSize?: number;
      options: {
        format?: string;
        timezone?: string;
      };
    },
  ): Promise<GlobalSchedulesNoMatchesReport> {
    return this.post(`/v2/audit/reports/schedules-no-matches`, data, {
      responseType: data.options.format === 'xls' ? 'arraybuffer' : undefined,
    });
  },

  reportGlobalConsecutiveSchedules<T>(
    this: BaseAPIClient,
    data: {
      stationsIds: string[];
      from: DateTimeStr;
      to: DateTimeStr;
      page?: number;
      pageSize?: number;
      options?: {
        format?: string;
      };
    },
  ): Promise<T> {
    return this.post<T>(`/v2/audit/reports/consecutive-schedules`, data, {
      responseType: data.options?.format === 'xls' ? 'arraybuffer' : undefined,
    });
  },

  plkParse(
    this: BaseAPIClient,
    {
      stationId,
      file,
    }: {
      stationId: string;
      file: File;
    },
  ) {
    const data = new FormData();
    data.append('file', file);

    return this.post<CreateScheduleDtoV2[]>(
      `/v2/stations/${stationId}/audit/schedules/plk-parse`,
      data,
    );
  },

  ascParse(
    this: BaseAPIClient,
    {
      stationId,
      file,
    }: {
      stationId: string;
      file: File;
    },
  ) {
    const data = new FormData();
    data.append('file', file);

    return this.post<CreateScheduleDtoV2[]>(
      `/v2/stations/${stationId}/audit/schedules/asc-parse`,
      data,
    );
  },

  configGet(this: BaseAPIClient) {
    return this.get<GetAuditConfigDto>(`/v2/audit/config`);
  },

  configUpdate(this: BaseAPIClient, data: UpdateAuditConfigDto) {
    return this.put<CreatePntsDto>(`/v2/audit/config`, data);
  },
};
